import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Reveal } from "@/components/Reveal";
import { GreekWatermark } from "@/components/GreekWatermark";
import { api, apiErrorMessage } from "@/lib/api";

const FACILITY_IMG = "/why-elysium/peter-coaching-closeup.jpg";
const TIME_OPTIONS = ["Morning", "Evening"];
const EXPERIENCE_OPTIONS = ["5+ Years", "0-5 Years", "None"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_time: "", experience: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", {
        ...form,
        message: `Free session request — Preferred time: ${form.preferred_time}, Training experience: ${form.experience}`,
      });
      toast.success("Thanks — we'll be in touch soon to book your free session.");
      setForm({ name: "", email: "", phone: "", preferred_time: "", experience: "" });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28 sm:pt-36 pb-20 sm:pb-28 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <GreekWatermark letter="Ψ" className="top-8 right-0" />
      <Reveal>
        <p className="font-accent italic text-gold text-lg mb-3">Get in touch</p>
        <h1 className="font-anton uppercase tracking-tighter text-cream text-4xl sm:text-6xl">Book Your First Free Coached Session</h1>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-14">
        <Reveal>
          <img src={FACILITY_IMG} alt="Elysium facility" className="w-full h-64 sm:h-80 object-cover border border-line" />
          <div className="mt-8 space-y-5 text-smoke">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gold mt-0.5 shrink-0" /> Unit 6, SA1 Business Park, Swansea, SA1 8DB
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-gold mt-0.5 shrink-0" /> 07305 864246
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-gold mt-0.5 shrink-0" /> elystrainingclub@gmail.com
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={handleSubmit} className="bg-surface border border-line p-6 sm:p-8 space-y-5" data-testid="contact-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">Full Name*</label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">Email Address*</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">Phone Number*</label>
              <Input
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">What's your preferred session time?*</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {TIME_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm({ ...form, preferred_time: opt })}
                    className={`h-12 border text-sm uppercase tracking-widest transition-colors ${
                      form.preferred_time === opt ? "border-gold bg-gold text-void" : "border-line text-smoke hover:border-gold/50"
                    }`}
                    data-testid={`contact-time-option-${opt.toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-smoke">What is your training experience?*</label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {EXPERIENCE_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setForm({ ...form, experience: opt })}
                    className={`h-12 border text-xs uppercase tracking-widest px-2 transition-colors ${
                      form.experience === opt ? "border-gold bg-gold text-void" : "border-line text-smoke hover:border-gold/50"
                    }`}
                    data-testid={`contact-experience-option-${opt.replace(/\s|\+/g, "").toLowerCase()}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting || !form.preferred_time || !form.experience}
              className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full h-12"
              data-testid="contact-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Book Your Session"}
            </Button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
